export interface PickCount {
  count: number;
  user: PlatformUser;
}

export type PickCounts = Record<string, PickCount>;

const STORAGE_PREFIX = 'tikit.peoplePicker.picks';
const MAX_REMEMBERED = 50;

export const pickCountsKey = (tenantId: string, userId: string) =>
  tenantId && userId ? `${STORAGE_PREFIX}.${tenantId}.${userId}` : '';

export const readPickCounts = (storage: Storage, key: string): PickCounts => {
  if (!key) return {};
  try {
    const parsed = JSON.parse(storage.getItem(key) ?? '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const byCountThenName = (a: PickCount, b: PickCount) =>
  b.count - a.count || (a.user?.FullName ?? '').localeCompare(b.user?.FullName ?? '');

export const recordPicks = (storage: Storage, key: string, picks: { key: string; user: PlatformUser }[]): PickCounts => {
  const counts = readPickCounts(storage, key);
  if (!key || picks.length === 0) return counts;

  for (const pick of picks) {
    counts[pick.key] = { count: (counts[pick.key]?.count ?? 0) + 1, user: pick.user };
  }

  const kept: PickCounts = Object.fromEntries(
    Object.entries(counts).sort(([, a], [, b]) => byCountThenName(a, b)).slice(0, MAX_REMEMBERED)
  );
  try {
    storage.setItem(key, JSON.stringify(kept));
  } catch {
    // guard clause - a full or blocked storage only costs the ranking, never the pick itself
  }
  return kept;
};

// Most-picked users first, the rest in the order given. A picked user is shown from the candidate
// list when it holds them, since the remembered copy is only as fresh as the last pick.
export const rankByPicks = (
  candidates: PlatformUser[],
  counts: PickCounts,
  keyOf: (user: PlatformUser) => string,
  isAllowed: (user: PlatformUser) => boolean
): PlatformUser[] => {
  const candidatesByKey = new Map(candidates.map((user) => [keyOf(user), user]));
  const picked = Object.entries(counts)
    .filter(([, pick]) => pick?.user && pick.count > 0)
    .sort(([, a], [, b]) => byCountThenName(a, b))
    .map(([key, pick]) => candidatesByKey.get(key) ?? pick.user)
    .filter(isAllowed);
  const pickedKeys = new Set(picked.map(keyOf));
  return [...picked, ...candidates.filter((user) => !pickedKeys.has(keyOf(user)))];
};
